import { useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'v2ThemeMode';
const DEFAULT_MODE_SETTING = 'system';

const isValidModeSetting = (value) => (
  value === 'system' || value === 'light' || value === 'dark'
);

const getSystemPrefersDark = () => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false;
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const getInitialModeSetting = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_MODE_SETTING;
  }

  const storedValue = window.localStorage.getItem(STORAGE_KEY);
  return isValidModeSetting(storedValue) ? storedValue : DEFAULT_MODE_SETTING;
};

export const useV2ThemeSettings = () => {
  const [modeSetting, setModeSetting] = useState(getInitialModeSetting);
  const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const updateSystemMode = (event) => {
      setSystemPrefersDark(event.matches);
    };

    // Support browsers with legacy MediaQueryList API.
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updateSystemMode);
    } else {
      mediaQuery.addListener(updateSystemMode);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', updateSystemMode);
      } else {
        mediaQuery.removeListener(updateSystemMode);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, modeSetting);
  }, [modeSetting]);

  const resolvedMode = useMemo(() => {
    if (modeSetting === 'system') {
      return systemPrefersDark ? 'dark' : 'light';
    }
    return modeSetting;
  }, [modeSetting, systemPrefersDark]);

  return {
    modeSetting,
    resolvedMode,
    setModeSetting,
  };
};

export default useV2ThemeSettings;
